// src/pages/patient/ManageDependents.js

import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, User,Users, AlertCircle } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { TextArea } from '../../components/ui/TextArea';
import Modal from '../../components/common/Modal';
import Alert from '../../components/ui/Alert';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import patientService from '../../services/patientService';

const ManageDependents = () => {
  const [dependents, setDependents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDependent, setSelectedDependent] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    bloodGroup: '',
    relationshipToParent: '',
    specialNeeds: '',
    schoolInfo: {
      schoolName: '',
      grade: '',
      schoolNurseContact: ''
    }
  });

  useEffect(() => {
    loadDependents();
  }, []);

  const loadDependents = async () => {
    try {
      setLoading(true);
      setError('');
      // ✅ FIXED: Use patientService.getDependents() instead of patientService.get()
      const response = await patientService.getDependents();
      
      if (response.success) {
        setDependents(response.data.dependents || []);
      }
    } catch (error) {
      console.error('Load dependents error:', error);
      setError('Failed to load dependents');
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (dateOfBirth) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const validateAge = (dateOfBirth) => {
    const age = calculateAge(dateOfBirth);
    return age < 18;
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    if (error) setError('');
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      gender: '',
      bloodGroup: '',
      relationshipToParent: '',
      specialNeeds: '',
      schoolInfo: {
        schoolName: '',
        grade: '',
        schoolNurseContact: ''
      }
    });
  };

  const openAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    resetForm();
    setError('');
  };

  const openEditModal = (dependent) => {
    setSelectedDependent(dependent);
    setFormData({
      firstName: dependent.firstName || '',
      lastName: dependent.lastName || '',
      dateOfBirth: dependent.dateOfBirth ? new Date(dependent.dateOfBirth).toISOString().split('T')[0] : '',
      gender: dependent.gender || '',
      bloodGroup: dependent.bloodGroup || '',
      relationshipToParent: dependent.relationshipToParent || '',
      specialNeeds: dependent.specialNeeds || '',
      schoolInfo: {
        schoolName: dependent.schoolInfo?.schoolName || '',
        grade: dependent.schoolInfo?.grade || '',
        schoolNurseContact: dependent.schoolInfo?.schoolNurseContact || ''
      }
    });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedDependent(null);
    resetForm();
    setError('');
  };

  const handleAddDependent = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.firstName || !formData.lastName || !formData.dateOfBirth || !formData.gender || !formData.relationshipToParent) {
      setError('Please fill in all required fields');
      return;
    }

    if (!validateAge(formData.dateOfBirth)) {
      setError('Dependent must be under 18 years old');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // ✅ FIXED: Use patientService.addDependent() instead of patientService.post()
      const response = await patientService.addDependent(formData);

      if (response.success) {
        setSuccess('Dependent added successfully');
        closeAddModal();
        await loadDependents();
        
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (error) {
      console.error('Add dependent error:', error);
      setError(error.message || 'Failed to add dependent');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDependent = async (e) => {
    e.preventDefault();
    
    if (!selectedDependent) return;

    // Validation
    if (!formData.firstName || !formData.lastName || !formData.dateOfBirth || !formData.gender || !formData.relationshipToParent) {
      setError('Please fill in all required fields');
      return;
    }

    if (!validateAge(formData.dateOfBirth)) {
      setError('Dependent must be under 18 years old');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // ✅ FIXED: Use patientService.updateDependent() instead of patientService.put()
      const response = await patientService.updateDependent(selectedDependent._id, formData);

      if (response.success) {
        setSuccess('Dependent updated successfully');
        closeEditModal();
        await loadDependents();
        
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (error) {
      console.error('Update dependent error:', error);
      setError(error.message || 'Failed to update dependent');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDependent = async (dependentId) => {
    if (!window.confirm('Are you sure you want to remove this dependent? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      // ✅ FIXED: Use patientService.removeDependent() instead of patientService.delete()
      const response = await patientService.removeDependent(dependentId);

      if (response.success) {
        setSuccess('Dependent removed successfully');
        await loadDependents();
        
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (error) {
      console.error('Delete dependent error:', error);
      setError(error.message || 'Failed to remove dependent');
    } finally {
      setLoading(false);
    }
  };

  const DependentCard = ({ dependent }) => (
    <div className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow p-6">
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-4 flex-1">
          <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center">
            <User className="w-8 h-8 text-primary-600" />
          </div>
          
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">
              {dependent.firstName} {dependent.lastName}
            </h3>
            
            <div className="mt-2 space-y-1">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Age:</span> {dependent.age} years old
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Gender:</span> {dependent.gender?.charAt(0).toUpperCase() + dependent.gender?.slice(1)}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Relationship:</span> {dependent.relationshipToParent?.charAt(0).toUpperCase() + dependent.relationshipToParent?.slice(1)}
              </p>
              {dependent.bloodGroup && (
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Blood Group:</span> {dependent.bloodGroup}
                </p>
              )}
              {dependent.schoolInfo?.schoolName && (
                <p className="text-sm text-gray-600">
                  <span className="font-medium">School:</span> {dependent.schoolInfo.schoolName}
                  {dependent.schoolInfo.grade && ` - ${dependent.schoolInfo.grade}`}
                </p>
              )}
            </div>

            {dependent.specialNeeds && (
              <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
                <p className="text-xs font-medium text-yellow-800 flex items-center">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  Special Needs
                </p>
                <p className="text-xs text-yellow-700 mt-1">{dependent.specialNeeds}</p>
              </div>
            )}

            <div className="mt-3 flex items-center space-x-4 text-xs text-gray-500">
              {dependent.allergies && dependent.allergies.length > 0 && (
                <span className="px-2 py-1 bg-red-100 text-red-800 rounded">
                  {dependent.allergies.length} Allergies
                </span>
              )}
              {dependent.consultationCount > 0 && (
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded">
                  {dependent.consultationCount} Consultations
                </span>
              )}
              {dependent.admissionCount > 0 && (
                <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded">
                  {dependent.admissionCount} Admissions
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col space-y-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => openEditModal(dependent)}
          >
            <Edit2 className="w-4 h-4 mr-1" />
            Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleDeleteDependent(dependent._id)}
            className="text-red-600 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Remove
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manage Dependents</h1>
          <p className="mt-1 text-sm text-gray-500">
            Add and manage children (under 18 years old) for booking consultations and tracking health records
          </p>
        </div>
        
        <Button onClick={openAddModal}>
          <Plus className="w-4 h-4 mr-2" />
          Add Dependent
        </Button>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <Alert type="success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {error && !showAddModal && !showEditModal && (
        <Alert type="error" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Dependents List */}
      {loading && !showAddModal && !showEditModal ? (
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : dependents.length > 0 ? (
        <>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex">
              <Users className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="ml-3">
                <p className="text-sm text-blue-800">
                  You have {dependents.length} dependent{dependents.length !== 1 ? 's' : ''} registered. 
                  You can book consultations and track health records for each dependent.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {dependents.map(dependent => (
              <DependentCard key={dependent._id} dependent={dependent} />
            ))}
          </div>
        </>
      ) : (
        <div className="text-center py-12 bg-white rounded-lg border">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No dependents added yet</h3>
          <p className="text-gray-500 mb-4">
            Add your children (under 18 years old) to book consultations and manage their health records
          </p>
          <Button onClick={openAddModal}>
            <Plus className="w-4 h-4 mr-2" />
            Add Your First Dependent
          </Button>
        </div>
      )}

      {/* Add Dependent Modal */}
      {showAddModal && (
        <Modal
          isOpen={showAddModal}
          onClose={closeAddModal}
          title="Add Dependent"
          size="lg"
        >
          <form onSubmit={handleAddDependent} className="space-y-6">
            {error && (
              <Alert type="error" onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* First Name */}
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                  First Name *
                </label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  required
                />
              </div>

              {/* Last Name */}
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name *
                </label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  required
                />
              </div>

              {/* Date of Birth */}
              <div>
                <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700 mb-2">
                  Date of Birth *
                </label>
                <Input
                  id="dateOfBirth"
                  type="date"
                  max={new Date().toISOString().split('T')[0]}
                  value={formData.dateOfBirth}
                  onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  Must be under 18 years old
                </p>
              </div>

              {/* Gender */}
              <div>
                <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-2">
                  Gender *
                </label>
                <Select
                  id="gender"
                  value={formData.gender}
                  onChange={(e) => handleInputChange('gender', e.target.value)}
                  required
                >
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </Select>
              </div>

              {/* Relationship */}
              <div>
                <label htmlFor="relationshipToParent" className="block text-sm font-medium text-gray-700 mb-2">
                  Relationship *
                </label>
                <Select
                  id="relationshipToParent"
                  value={formData.relationshipToParent}
                  onChange={(e) => handleInputChange('relationshipToParent', e.target.value)}
                  required
                >
                  <option value="">Select relationship</option>
                  <option value="son">Son</option>
                  <option value="daughter">Daughter</option>
                  <option value="stepson">Stepson</option>
                  <option value="stepdaughter">Stepdaughter</option>
                  <option value="ward">Ward</option>
                  <option value="other">Other</option>
                </Select>
              </div>

              {/* Blood Group */}
              <div>
                <label htmlFor="bloodGroup" className="block text-sm font-medium text-gray-700 mb-2">
                  Blood Group (Optional)
                </label>
                <Select
                  id="bloodGroup"
                  value={formData.bloodGroup}
                  onChange={(e) => handleInputChange('bloodGroup', e.target.value)}
                >
                  <option value="">Select blood group</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="AB+">AB+</option>
                  <option value="AB-">AB-</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                </Select>
              </div>
            </div>

            {/* School Information */}
            <div className="border-t pt-4">
              <h3 className="text-sm font-medium text-gray-900 mb-4">School Information (Optional)</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="schoolName" className="block text-sm font-medium text-gray-700 mb-2">
                    School Name
                  </label>
                  <Input
                    id="schoolName"
                    value={formData.schoolInfo.schoolName}
                    onChange={(e) => handleInputChange('schoolInfo.schoolName', e.target.value)}
                  />
                </div>

                <div>
                  <label htmlFor="grade" className="block text-sm font-medium text-gray-700 mb-2">
                    Grade/Class
                  </label>
                  <Input
                    id="grade"
                    value={formData.schoolInfo.grade}
                    onChange={(e) => handleInputChange('schoolInfo.grade', e.target.value)}
                  />
                </div>

                <div>
                  <label htmlFor="schoolNurseContact" className="block text-sm font-medium text-gray-700 mb-2">
                    School Nurse Contact
                  </label>
                  <Input
                    id="schoolNurseContact"
                    value={formData.schoolInfo.schoolNurseContact}
                    onChange={(e) => handleInputChange('schoolInfo.schoolNurseContact', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Special Needs */}
            <div>
              <label htmlFor="specialNeeds" className="block text-sm font-medium text-gray-700 mb-2">
                Special Needs or Medical Considerations (Optional)
              </label>
              <TextArea
                id="specialNeeds"
                rows={3}
                placeholder="Any special needs, allergies, or medical considerations..."
                value={formData.specialNeeds}
                onChange={(e) => handleInputChange('specialNeeds', e.target.value)}
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={closeAddModal}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                loading={loading}
              >
                {loading ? 'Adding...' : 'Add Dependent'}
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {/* Edit Dependent Modal */}
      {showEditModal && selectedDependent && (
        <Modal
          isOpen={showEditModal}
          onClose={closeEditModal}
          title="Edit Dependent"
          size="lg"
        >
          <form onSubmit={handleUpdateDependent} className="space-y-6">
            {error && (
              <Alert type="error" onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            {/* Same form fields as Add Modal */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* First Name */}
              <div>
                <label htmlFor="editFirstName" className="block text-sm font-medium text-gray-700 mb-2">
                  First Name *
                </label>
                <Input
                  id="editFirstName"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  required
                />
              </div>

              {/* Last Name */}
              <div>
                <label htmlFor="editLastName" className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name *
                </label>
                <Input
                  id="editLastName"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  required
                />
              </div>

              {/* Date of Birth */}
              <div>
                <label htmlFor="editDateOfBirth" className="block text-sm font-medium text-gray-700 mb-2">
                  Date of Birth *
                </label>
                <Input
                  id="editDateOfBirth"
                  type="date"
                  max={new Date().toISOString().split('T')[0]}
                  value={formData.dateOfBirth}
                  onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                  required
                />
              </div>

              {/* Gender */}
              <div>
                <label htmlFor="editGender" className="block text-sm font-medium text-gray-700 mb-2">
                  Gender *
                </label>
                <Select
                  id="editGender"
                  value={formData.gender}
                  onChange={(e) => handleInputChange('gender', e.target.value)}
                  required
                >
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </Select>
              </div>

              {/* Relationship */}
              <div>
                <label htmlFor="editRelationship" className="block text-sm font-medium text-gray-700 mb-2">
                  Relationship *
                </label>
                <Select
                  id="editRelationship"
                  value={formData.relationshipToParent}
                  onChange={(e) => handleInputChange('relationshipToParent', e.target.value)}
                  required
                >
                  <option value="">Select relationship</option>
                  <option value="son">Son</option>
                  <option value="daughter">Daughter</option>
                  <option value="stepson">Stepson</option>
                  <option value="stepdaughter">Stepdaughter</option>
                  <option value="ward">Ward</option>
                  <option value="other">Other</option>
                </Select>
              </div>

              {/* Blood Group */}
              <div>
                <label htmlFor="editBloodGroup" className="block text-sm font-medium text-gray-700 mb-2">
                  Blood Group (Optional)
                </label>
                <Select
                  id="editBloodGroup"
                  value={formData.bloodGroup}
                  onChange={(e) => handleInputChange('bloodGroup', e.target.value)}
                >
                  <option value="">Select blood group</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="AB+">AB+</option>
                  <option value="AB-">AB-</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                </Select>
              </div>
            </div>

            {/* School Information */}
            <div className="border-t pt-4">
              <h3 className="text-sm font-medium text-gray-900 mb-4">School Information (Optional)</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="editSchoolName" className="block text-sm font-medium text-gray-700 mb-2">
                    School Name
                  </label>
                  <Input
                    id="editSchoolName"
                    value={formData.schoolInfo.schoolName}
                    onChange={(e) => handleInputChange('schoolInfo.schoolName', e.target.value)}
                  />
                </div>

                <div>
                  <label htmlFor="editGrade" className="block text-sm font-medium text-gray-700 mb-2">
                    Grade/Class
                  </label>
                  <Input
                    id="editGrade"
                    value={formData.schoolInfo.grade}
                    onChange={(e) => handleInputChange('schoolInfo.grade', e.target.value)}
                  />
                </div>

                <div>
                  <label htmlFor="editSchoolNurse" className="block text-sm font-medium text-gray-700 mb-2">
                    School Nurse Contact
                  </label>
                  <Input
                    id="editSchoolNurse"
                    value={formData.schoolInfo.schoolNurseContact}
                    onChange={(e) => handleInputChange('schoolInfo.schoolNurseContact', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Special Needs */}
            <div>
              <label htmlFor="editSpecialNeeds" className="block text-sm font-medium text-gray-700 mb-2">
                Special Needs or Medical Considerations (Optional)
              </label>
              <TextArea
                id="editSpecialNeeds"
                rows={3}
                placeholder="Any special needs, allergies, or medical considerations..."
                value={formData.specialNeeds}
                onChange={(e) => handleInputChange('specialNeeds', e.target.value)}
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={closeEditModal}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                loading={loading}
              >
                {loading ? 'Updating...' : 'Update Dependent'}
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default ManageDependents;